import React, { useMemo, useState } from "react";
import { Container } from "react-grid-system";
import { LayoutContainer, StyledLayout } from "./styles";
import Footer from "../../components/CSMFooter/CSMFooter";
import CSMShopfloorHeader from "../../components/CSMShopfloorHeader/CSMShopfloorHeader";
import TOSHeader from "../../pages/TOSPage/TOSHeader";
import TOSFooter from "../../pages/TOSPage/TOSFooter";
import GlobalSearch from "../../components/GlobalSearch/GlobalSearch";
import LastSync from "../../components/LastSyncHeader/LastSync";
import GlobalFilter from "../../components/GlobalFilter/GlobalFilter";
import { useLocation, matchPath } from "react-router-dom";
import { useStoreState } from "../../store";
import Ribbon from "../../components/CSMRibbon/Ribbon";
import CSMManagerModeToggle from "../../components/CSMManagerModeToggle/CSMManagerModeToggle";
import CSMTableControls from "../../components/CSMTableControls/CSMTableControls";
import { GlobalLoader } from "../../components/GlobalLoader/GlobalLoader";
import { useHasAcceptedTos } from "../../utility/useHasAcceptedTos";

interface IProps {
  children: React.ReactNode;
  hideHeader?: boolean;
}

export const DefaultLayout: React.FC<IProps> = ({
  children,
  hideHeader
}) => {
  const location = useLocation();

  /** ✅ APP STATE */
  const isManagerMode = useStoreState(
    (s) => s.appSettings.isManagerMode
  );

  const isActiveJobMode = useStoreState(
    (s) => s.appSettings.isActiveJobMode
  );

  const jobs = useStoreState(
    (s) => s.appSettings.jobs
  );

  /** ✅ Derived: completed jobs count */
const completedCount = useMemo(() => {
  if (!jobs || jobs.length === 0) return 0;

  const completedStatuses = ["COMPLETED", "COMPLETED2", "CLOSED"];

  return jobs.filter(j => completedStatuses.includes(j.operation)).length;
}, [jobs]);


  /**
   * ✅ TOS state.
   *
   * The read, the same-tab `consentAccepted` event and the cross-tab `storage` event moved to
   * `utility/useHasAcceptedTos` when a third reader appeared (`SupportAssistantHost`, which is
   * mounted above the router and so cannot be handed this component's state). `pages/Home` had
   * its own copy of the same logic and now shares this one.
   */
  const hasAcceptedTOS = useHasAcceptedTos();

  const [filtersOpen, setFiltersOpen] = useState(true);

  const isDiagnostic = useMemo(() => {
    return Boolean(
      matchPath({ path: "/diagnostic/*", end: false }, location.pathname)
    );
  }, [location.pathname]);

  /**
   * ✅ Global Search & Filter rules
   */
  const canShowGlobalControls =
    hasAcceptedTOS &&
    location.pathname === "/home" &&
    !isDiagnostic &&
    (
      !isManagerMode ||
      (isManagerMode && !isActiveJobMode)
    );

      const canShowGlobalLastSync =
    hasAcceptedTOS &&
    location.pathname === "/home" &&
    !isDiagnostic &&
    (
      !isManagerMode 
    );

  const showRibbon =
    hasAcceptedTOS &&
    new Set(["/system", "/users", "/operations", "/Analytics"]).has(location.pathname) &&
    isManagerMode &&
    !isDiagnostic;

  const showManagerHomeToggle =
    hasAcceptedTOS &&
    location.pathname === "/home" &&
    isManagerMode &&
    !isDiagnostic;

  const showHeader = !hideHeader && !isDiagnostic;
  const showFooter = !isDiagnostic;

  return (
    <StyledLayout>
      <Container fluid id="layout-container">

        {/* ✅ Header */}
        {showHeader &&
          (hasAcceptedTOS ? <CSMShopfloorHeader /> : <TOSHeader />)
        }

        {/* ✅ Manager Mode Toggle w/ Completed Count */}
        {/* The wrapper's top padding is the breathing room between the header
            and the Active/Completed jobs tabs (was 8px). */}
        {showManagerHomeToggle && (
          <div style={{ padding: "20px 0 0 0" }}>
            <CSMManagerModeToggle
              completedCount={completedCount}
              onChange={() => {}}
            />
          </div>
        )}

        {showRibbon && <Ribbon />}

        {/* ✅ Global Controls */}
        {canShowGlobalControls && (
          <>
            <GlobalSearch
              filtersOpen={filtersOpen}
              onToggleFilters={setFiltersOpen}
            />
            {filtersOpen && <GlobalFilter />}
          </>
        )}

         {canShowGlobalControls && (
          <>
            <LastSync
            />
          </>
        )}

        <LayoutContainer>
          <GlobalLoader>
          <div id = "page-body" className="content-scrollable">
            
            {children}
            
          </div>
          </GlobalLoader>
        </LayoutContainer>

      </Container>

      {/* ✅ Footer */}
      {showFooter &&
        (hasAcceptedTOS ? <Footer /> : <TOSFooter />)
      }

      {/* ⚠️ THE SUPPORT ASSISTANT IS NO LONGER MOUNTED HERE — see
          components/SupportAssistant/SupportAssistantHost.tsx. This layout renders inside
          App.tsx's single <Suspense> boundary, so the first navigation to an unvisited lazy
          route unmounted the panel and threw away the conversation in it. Both conditions this
          line used to apply (terms accepted, not on /diagnostic) moved to the host with it.
          Do not re-add it here. */}
    </StyledLayout>
  );
};

export default DefaultLayout;
